import React, { useCallback, useMemo, useRef } from 'react';
import type { TabNavItem, TabNavMenuItem, TabNavOverrides } from '@withjoy/joykit/components/TabNav';
import { TabNav } from '@withjoy/joykit/components/TabNav';
import { Flex } from '@withjoy/joykit';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { ADMIN_MENU_STYLE_OVERRIDES, LinkWithTelemetry, NavigateRefContext } from '@apps/admin/components/adminTabNavShared';
import { useNavItems } from './useNavItems';
import { useTranslation } from '@shared/core';
const CATEGORY_TAB_NAV_OVERRIDES: TabNavOverrides = {
  Root: {
    props: {
      width: '100%',
      minWidth: 0,
    },
  },
  List: {
    props: {
      width: '100%',
      justifyContent: { _: 'flex-start', md: 'center' },
      overflowX: { _: 'auto', md: 'visible' },
      paddingX: { _: 6, md: 0 },
      paddingTop: '6px',
      borderBottomWidth: 0,
      whiteSpace: 'nowrap',
      style: { scrollbarWidth: 'none' },
    },
  },
};

const MENU_POPPER_OPTIONS = { strategy: 'fixed' } as const;

export const CategoryNav = () => {
  const { navItems } = useNavItems();
  const { viewCategoryDesigns } = useCardTelemetry();
  const { tq } = useTranslation('stationery');

  const hrefToCategoryMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of navItems) {
      if (item.type === 'category') {
        map[item.to] = item.category as string;
      } else {
        map[`#${item.category}`] = item.category as string;
        for (const child of item.children) {
          map[child.to] = child.category;
        }
      }
    }
    return map;
  }, [navItems]);

  const onNavigateRef = useRef<(href: string) => void>(() => {});
  onNavigateRef.current = (href: string) => {
    const category = hrefToCategoryMap[href];
    if (category) {
      viewCategoryDesigns(category);
    }
  };

  const handleTabChange = useCallback(
    (item: TabNavItem) => {
      const category = hrefToCategoryMap[item.href];
      if (category) {
        viewCategoryDesigns(category);
      }
    },
    [hrefToCategoryMap, viewCategoryDesigns],
  );

  const tabItems: ReadonlyArray<TabNavItem> = useMemo(() => {
    return navItems.map(item => {
      if (item.type === 'subcategory') {
        const menuItems: ReadonlyArray<TabNavMenuItem> = item.children.map(child => ({
          label: child.title,
          href: child.to,
        }));
        return {
          label: item.title,
          href: `#${item.category}`,
          isActive: item.isActive(),
          menu: menuItems,
        };
      }
      return {
        label: item.title,
        href: item.to,
        isActive: item.isActive(),
      };
    });
  }, [navItems]);

  return (
    <NavigateRefContext.Provider value={onNavigateRef}>
      <Flex
        justifyContent="center"
        borderBottom="1px solid"
        borderColor="mono3"
        position="relative"
        zIndex={2}
      >
        <TabNav
          items={tabItems}
          linkComponent={LinkWithTelemetry}
          menuStyleOverrides={ADMIN_MENU_STYLE_OVERRIDES}
          menuPopperOptions={MENU_POPPER_OPTIONS}
          menuUsePortal
          aria-label={tq`Category navigation`}
          data-testid="category-tab-nav"
          overrides={CATEGORY_TAB_NAV_OVERRIDES}
          onTabChange={handleTabChange}
        />
      </Flex>
    </NavigateRefContext.Provider>
  );
};
